import { workspaceTextFor, type WorkspaceText } from './interface-copy';
import { useWorkspaceText } from './interface-copy';
import collaborationCopy from '@/i18n/ns/workspace-collaboration';
import { namespaceCopyFor } from '@/i18n/ns';
// Shared chats inside the thread (collaboration v1, PR 2 §6, §6b, §6c, §7):
// the read-only bar that replaces a reader's composer, suggested follow-ups
// (the reader's field, the owner's chips), the "Suggested by" label, the live
// "Anna is getting an answer" line, Fork from here, and "From Anna's Gmail" on
// a tool result from someone else's private account. Only the owner writes:
// nothing a reader does here starts a turn in someone else's chat, and
// nothing the owner does here sends one either — Ask only fills her composer.
import { useEffect, useState, useSyncExternalStore, type FormEvent } from 'react';
import { ActionBarMorePrimitive, useAuiState, useComposerRuntime } from '@assistant-ui/react';
import { CompanyRequestError } from '@/company/api';
import { useOptionalWorkspace, useWorkspace } from './context';
import { WorkspaceIcon } from './icons';
import { MessageContextChips } from './message-context-chips';
import { WorkspaceQuestionDock } from './question-dock';
import { RunFollowBar } from './agents/run-follow-bar';
import { RunOutcomeFooter } from './agents/run-outcome-footer';
import { RunTaskMessage } from './agents/run-card';
import { ForkButton, firstName, forkAndOpen } from './chat-collaboration';
import {
  chatCollaborationApi,
  readAskedSuggestion,
  readSentSuggestions,
  setAskedSuggestion,
  subscribeAskedSuggestions,
  type FollowUpSuggestion,
} from './chat-collaboration-api';

function useFollowUps(threadId: string, enabled: boolean) {
  const [list, setList] = useState<FollowUpSuggestion[]>([]);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!enabled) {
      setList([]);
      return;
    }
    let active = true;
    void chatCollaborationApi
      .followUps(threadId)
      .then((value) => {
        if (active) setList(value.suggestions);
      })
      .catch(() => {
        if (active) setList([]);
      });
    return () => {
      active = false;
    };
  }, [threadId, enabled, version]);
  return { list, reload: () => setVersion((value) => value + 1) };
}

const suggestError = (
  failure: unknown,
  owner: string,
  uiText: WorkspaceText = workspaceTextFor('en'),
) =>
  failure instanceof CompanyRequestError && failure.code === 'follow_up_limit'
    ? uiText('You have five open suggestions here. {value1} will see them.', { value1: owner })
    : uiText('We could not send your suggestion. Please try again.');

/** The reader's composer: who writes here, the suggestion field and Fork. */
export function ReadOnlyBar() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const meta = ws.threadMeta;
  const owner = firstName(meta?.ownerName, uiText);
  const { list, reload } = useFollowUps(ws.threadId, ws.readOnly);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  async function submit(event: FormEvent, uiText: WorkspaceText = workspaceTextFor('en')) {
    event.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      await chatCollaborationApi.suggest(ws.threadId, text.trim());
      setText('');
      setSent(true);
      reload();
    } catch (failure) {
      setError(suggestError(failure, owner, uiText));
    } finally {
      setBusy(false);
    }
  }
  const suggested = sent || list.length > 0;
  return (
    <div className="ws-readonly-bar" role="region" aria-label={uiText('Read-only chat')}>
      <div className="ws-readonly-lead">
        <WorkspaceIcon name="eye" size={16} />
        <p>
          {uiRich(
            'Only {value1} writes in this chat. Fork it to continue with your own connections. {value2}',
            {
              value1: <b>{meta?.ownerName ?? owner}</b>,
              value2: suggested && (
                <span className="ws-readonly-status" role="status">
                  {uiRich('{value1} {value2} · {value3} will see it.', {
                    value1: ' ',
                    value2: <b>{uiText('Suggested')}</b>,
                    value3: owner,
                  })}
                </span>
              ),
            },
          )}
        </p>
        <ForkButton />
      </div>
      <form className="ws-suggest-form" onSubmit={(event) => void submit(event, uiText)}>
        <input
          type="text"
          value={text}
          maxLength={500}
          placeholder={uiText('Suggest a follow-up…')}
          aria-label={uiText('Suggest a follow-up')}
          onChange={(event) => setText(event.target.value)}
        />
        <button className="ws-button" type="submit" disabled={busy || !text.trim()}>
          {busy ? uiText('Sending…') : uiText('Send')}
        </button>
      </form>
      {error && (
        <p className="ws-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function useAskedSuggestion(threadId: string) {
  return useSyncExternalStore(
    subscribeAskedSuggestions,
    () => readAskedSuggestion(threadId),
    () => null,
  );
}

// The suggestions whose turn this tab already sent: their chips stay hidden,
// so the owner never asks the same suggestion twice.
function useSentSuggestions(threadId: string) {
  return useSyncExternalStore(
    subscribeAskedSuggestions,
    () => readSentSuggestions(threadId),
    () => readSentSuggestions(''),
  );
}

/** The owner's open suggestions, as chips above the composer. Ask puts the
 * text in the composer tagged "Suggested by …" and sends nothing; × dismisses.
 * Never the send-on-click follow-up chip: a reader's text must not become a
 * one-click turn with the owner's private access. */
export function SuggestedFollowUps() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const composer = useComposerRuntime();
  const running = useAuiState((state) => state.thread.isRunning);
  const owner = !ws.readOnly && ws.threadMeta?.access === 'owner';
  const { list, reload } = useFollowUps(ws.threadId, owner);
  const asked = useAskedSuggestion(ws.threadId);
  const sent = useSentSuggestions(ws.threadId);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const visible = list.filter(
    (item) => !hidden.has(item.id) && !sent.has(item.id) && item.id !== asked?.id,
  );
  if (!owner || (!visible.length && !asked)) return null;
  const hide = (id: string) => setHidden((current) => new Set([...current, id]));
  async function dismiss(item: FollowUpSuggestion) {
    hide(item.id);
    try {
      await chatCollaborationApi.dismiss(ws.threadId, item.id);
    } catch {
      ws.notify(uiText('We could not dismiss that suggestion. Please try again.'));
    }
    reload();
  }
  return (
    <div className="ws-follow-ups" aria-label={uiText('Suggested follow-ups')}>
      {visible.map((item) => (
        <div key={item.id} className="ws-follow-up-chip">
          <span className="ws-follow-up-avatar" aria-hidden="true">
            {(item.suggestedBy.name ?? '?').slice(0, 1).toUpperCase()}
          </span>
          <p>
            <small>
              {uiRich('{value1} suggests', { value1: firstName(item.suggestedBy.name, uiText) })}
            </small>
            <span>{item.text}</span>
          </p>
          <button
            className="ws-button primary"
            disabled={running}
            onClick={() => {
              // Fill, never send: the owner reads, edits and presses Send.
              composer.setText(item.text);
              setAskedSuggestion(ws.threadId, { id: item.id, name: item.suggestedBy.name });
            }}
          >
            {uiText('Ask')}
          </button>
          <button
            className="ws-icon-button"
            aria-label={uiText('Dismiss {value1}’s suggestion', {
              value1: firstName(item.suggestedBy.name, uiText),
            })}
            onClick={() => void dismiss(item)}
          >
            <WorkspaceIcon name="close" size={14} />
          </button>
        </div>
      ))}
      {asked && (
        <p className="ws-suggested-tag" role="status">
          <WorkspaceIcon name="users" size={12} />
          {uiText('Suggested by')}
          {firstName(asked.name, uiText)} {uiText('· edit it or send it')}
          <button
            className="ws-icon-button"
            aria-label={uiText('Remove the suggestion tag')}
            onClick={() => setAskedSuggestion(ws.threadId, null)}
          >
            <WorkspaceIcon name="close" size={12} />
          </button>
        </p>
      )}
    </div>
  );
}

/** Above the composer: the owner's suggested follow-ups, then the question dock. */
export function WorkspaceComposerDock() {
  return (
    <>
      <SuggestedFollowUps />
      <WorkspaceQuestionDock />
    </>
  );
}

/** Who is answering, in the words of the live line: the chat's owner, or,
 * in the owner's own run chat, the agent. */
function answeringName(
  ws: ReturnType<typeof useWorkspace>,
  uiText: WorkspaceText = workspaceTextFor('en'),
) {
  const meta = ws.threadMeta;
  if (meta?.access === 'owner' && meta.agentRunId) return uiText('The agent');
  return firstName(meta?.ownerName, uiText);
}

/** Under the last message of a followed chat: "Anna is getting an answer"
 * while her turn runs, "Anna stopped the answer" after she stopped it. */
export function LiveStatus() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const running = useAuiState((state) => state.thread.isRunning);
  const live = ws.threadLive;
  const name = answeringName(ws, uiText);
  if (running || live?.active)
    return (
      <p className="ws-live-status" role="status">
        {uiRich('{value1} {value2} is getting an answer', {
          value1: <span className="ws-live-dot" aria-hidden="true" />,
          value2: name,
        })}
      </p>
    );
  if (live?.stopped)
    return (
      <p className="ws-live-status stopped" role="status">
        {uiRich('{value1} {value2} stopped the answer', {
          value1: <WorkspaceIcon name="close" size={12} />,
          value2: name,
        })}
      </p>
    );
  return <RunOutcomeFooter />;
}

/** "Fork from here" in an answer's More menu: a copy of the chat up to and
 * including this message, as the person's own chat. */
export function ForkFromHereItem() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const messageId = useAuiState((state) => state.message.id);
  if (!ws.threadMeta) return null;
  return (
    <ActionBarMorePrimitive.Item
      className="aui-action-bar-more-item hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm outline-none select-none"
      onSelect={() => void forkAndOpen(ws, ws.threadId, messageId, uiText)}
    >
      {uiRich('{value1} Fork from here', { value1: <WorkspaceIcon name="fork" size={16} /> })}
    </ActionBarMorePrimitive.Item>
  );
}

// Whose private account a tool result came from, when it is not the
// reader's own: the server's tool-call record, and for a running turn the
// live state's recorded calls so far.
function useToolSources(toolCallIds: string[]) {
  const { text: uiText } = useWorkspaceText();
  const ws = useOptionalWorkspace();
  const labels = new Map<string, { person: string; account: string }>();
  for (const id of toolCallIds) {
    const source = ws?.threadMeta?.toolSources[id] ?? ws?.threadLive?.toolSources[id];
    if (!source || source.userId === ws?.user?.id) continue;
    labels.set(`${source.userId}:${source.provider}`, {
      person: firstName(source.name, uiText),
      account: source.provider ?? 'account',
    });
  }
  return [...labels.values()];
}

function SourceLine({ label }: { label: { person: string; account: string } }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  return (
    <span
      className="ws-tool-source"
      title={uiText('Only {value1} can use this account', { value1: label.person })}
    >
      {uiRich('{value1} From {value2}’s {value3}', {
        value1: <WorkspaceIcon name="lock" size={11} />,
        value2: label.person,
        value3: label.account,
      })}
    </span>
  );
}

/** "From Anna's Gmail" on a tool result that came from someone else's
 * private account. Nothing for the reader's own or a company account. */
export function ToolSourceLabel({ toolCallId }: { toolCallId: string }) {
  const [label] = useToolSources([toolCallId]);
  return label ? <SourceLine label={label} /> : null;
}

/** The same, once per account, under a collapsed "N tool calls" row, so it
 * is clear whose data it is without opening the row. */
export function ToolGroupSources({ toolCallIds }: { toolCallIds: string[] }) {
  const labels = useToolSources(toolCallIds);
  return labels.length ? (
    <span className="ws-tool-sources">
      {labels.map((label) => (
        <SourceLine key={`${label.person}:${label.account}`} label={label} />
      ))}
    </span>
  ) : null;
}

/** The thread slots collaboration v1 changes (ui/app.tsx and the UI tests use
 * this one list): "Suggested by" on user messages, the owner's suggestion
 * chips over the composer, Fork from here in each answer's menu and, when the
 * pane follows someone's running turn — a reader of someone else's chat, or
 * the owner watching their own agent run — the live line, the "New answer"
 * pill and a bar instead of the composer, with nothing that starts a turn. */
export function collaborationThreadComponents({
  readOnly,
  following = readOnly,
  keepScrollTop = null,
}: {
  readOnly: boolean;
  following?: boolean;
  keepScrollTop?: number | null;
}) {
  return {
    ComposerDock: WorkspaceComposerDock,
    UserMessageHeader: UserMessageExtras,
    // A run chat opens with its run card in place of the run's internal task message.
    UserMessageWrapper: RunTaskMessage,
    MessageMenuItems: ForkFromHereItem,
    ToolGroupNote: ToolGroupSources,
    // A finished run chat is not followed, but its record may still say Failed.
    AfterMessages: following ? LiveStatus : RunOutcomeFooter,
    ...(following ? { newAnswerPill: true, keepScrollTop } : {}),
    ...(readOnly ? { Composer: ReadOnlyBar, readOnly: true } : {}),
    ...(!readOnly && following ? { Composer: RunFollowBar, readOnly: true } : {}),
  };
}

/** Above a sent message: what was attached to it, then who suggested it. */
function UserMessageExtras() {
  return (
    <>
      <MessageContextChips />
      <SuggestedByLabel />
    </>
  );
}

/** "Suggested by Erik" on a user message that asked a suggested follow-up.
 * The name comes from the server's suggestion row, never the message parts. */
export function SuggestedByLabel() {
  const { text: uiText, rich: uiRich, locale } = useWorkspaceText();
  const copy = namespaceCopyFor(collaborationCopy, locale);
  const ws = useOptionalWorkspace();
  const messageId = useAuiState((state) => state.message.id);
  const by = ws?.threadMeta?.suggestedBy[messageId];
  if (!by) return null;
  const name = by.userId === ws?.user?.id ? copy.suggestedYou : firstName(by.name, uiText);
  return (
    <p className="ws-suggested-by">
      {uiRich('{value1} Suggested by {value2}', {
        value1: <WorkspaceIcon name="users" size={12} />,
        value2: name,
      })}
    </p>
  );
}
